/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
    createStyles,
    WithStyles,
    withStyles
} from '@mui/styles';
import { Theme } from '@mui/material/styles';
import {
    inject,
    observer
} from 'mobx-react';
import * as React from 'react';
import { RootStore } from '../../stores/root.store';
import { SimpleList, SimpleListItem } from '../../shared/components/simple-list';
import { RunningJobsList } from '../../components/running-jobs-list';
import {
    AdminJobTypes,
    JobRequest,
    JobResult,
    JobScheduleResult,
    QRCodeGenerationJobResult,
    // JobCreationResult,
    RunningJobs,
    STATIC_PROGRESS_LIMIT,
    StatusTypes
} from '../../models/admin-jobs';
import { MainTabs } from '../../stores/manager.store';
import { PageBoundary, SimpleListPagination } from '../../shared/components/simple-list-pagination';
import { CCSnackbarVariant } from '../../shared/components/cc-snackbar';
import { Button, CardActions, CardContent, Divider, Grid, Paper, Switch, TextField, Typography } from '@mui/material';
import { SearchInput } from '../../shared/components/search-input';
import { LeafletPDFClient } from '../../shared/components/leaflet-pdf-client';
import { RenderTree } from '../../shared/components/node-tree';
import { EntityFloorplan, Floorplan, UserTeam } from '../../shared/domain';
import { LeafletPDFData, MarkerOperation } from '../../shared/domain/leaflet';
import { REPORTS } from '../../constants';
import { SearchResults } from '../../adapters/report-base-adapter';
import { TabControl, TabDescriptor } from '../../shared/components/tab-control';
import { CCSpinner } from '../../shared/components/cc-spinner';
import { FolderTree } from '../../components/folder-tree';
import { SimpleModal } from '../../shared/components/simple-modal';
import { v4 as uuidv4 } from 'uuid';
import { SideNavbar } from '../../shared/components/side-navbar';
import { HourglassBottom } from '@mui/icons-material';
import { Marker } from '../../models/marker';
import { QRTemplateResponse, TabTypes } from '../../models/admin';

const TASKS_ROWS_PER_PAGE = 10;
const TASKS_PAGING_LIMIT = 100;
const FOLDER_PERMISSIONS = 'route_read,route_write';
const ITEMS_PAGING_LIMIT = 20;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const styles = (theme: Theme) => createStyles({
    root: {
        width: '100%',
        position: 'relative',
    },
    tabContainer: {
        padding: '0.1em 0',
        width: '100%'
    },
    divider: {
        margin: '1.6em 0 1.55em 0',
    },
    bodyTitle: {
        marginBottom: '0.4em',
    },
    addTeamContainer: {
        padding: '2em'
    },
    treeContainer: {
        padding: '2em',
        flex: '1',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '1em'
    },
    treeSection: {
        width: '50em',
        height: '100%'
    },
    QRSection: {
        width: '33%',
        height: '100%'
    },
    QRSectionSmall: {
        width: '25%',
        height: '100%'
    },
    treeTitle: {
        marginTop: '1em',
        display: 'flex',
        alignItems: 'center',
        paddingBottom: '0.2em',
        borderBottom: '1px solid'
    },
    thinClientContainer: {
        height: '50em',
        marginBottom: '3em',
        transition: 'width 1s',
        flex: '1',
        [theme.breakpoints.down('lg')]: {
            maxHeight: '35em'
        }
    },
    fullScreenContainer: {
        top: '6.4em',
        height: '86%'
    },
    animContainer: {
        transition: 'width 1s',
        marginTop: '0.5em'
    },
    mapWrapper: {
        height: '92%',
        width: '100%',
        minHeight: '35em',
        minWidth: '50em',
        margin: '2em 0'
    },
    tabRoot: {
        '& .MuiTabs-root': {
            paddingLeft: '5em'
        }
    },
    spinner: {
        zIndex: 1301,
        position: 'fixed',
    },
    searchRoot: {
        width: '100%'
    },
    tasksContainerSize: {
        flex: 1,
        '& .MuiListItem-root': {
            padding: '0.5em !important'
        }
    },
    routeNavButton: {
        width: '100%',
        height: '4em',
        marginBottom: '1em',
        '& .MuiSvgIcon-root': {
            marginRight: '0.2em',
            transform: 'scale(0.9)'
        }
    },
    navbarIconContainer: {
        padding: '0.3em 0.4em',
        backgroundColor: theme.palette.secondary.main,
    },
    navbarIconContainerSmall: {
        transform: 'scale(0.7)',
        margin: '0.7em',
    },
    navbarIcon: {
        color: 'white',
    },
    navbarContent: {
        display: 'flex',
        flex: '1 0 0',
        flexDirection: 'column',
        width: '30em',
    },
    navbarTitleContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    navbarTitle: {
        fontWeight: 'bold',
        marginLeft: '0.4em',
    },
    contentContainer: {
        '&, &$navbarContent': {
            padding: '1.45em',
        },
        [theme.breakpoints.between(
            'xs',
            theme.breakpoints.values.iPhone8_Landscape
        )]: {
            padding: '0',
        },
    },
    navbarRoot: {
        position: 'absolute',
        height: '3.2em',
        width: '4.5em',
        top: '0.1em',
    },
    navbarToogleButton: {
        marginTop: '-0.3em',
    },
    modal: {
        minWidth: '30em',
        maxWidth: '35em',
        padding: '0.5em',
        borderRadius: '0.4em'
    },
    modalFooter: {
        alignItems: 'center',
        flex: 1,
        padding: '1em',
        display: 'flex',
        justifyContent: 'space-between'
    },
    modalTitle: {
        marginTop: '1em',
        display: 'flex',
        alignItems: 'center',
        paddingBottom: '0.2em',
        borderBottom: '1px solid'
    },
    modalContainer: {
        padding: '1.5em 0 0',
        width: '100%',
        height: '100%'
    },
    operationsList: {
        paddingTop: '1em',
    },
    simpleList: {
        overflow: 'auto',
        width: '100%',
        maxHeight: '90%',
        marginTop: '1em'
    },
    tooltipText: {
        marginTop: 0,
        fontStyle: 'italic',
        color: 'gray',
    },
    fontInput: {
        display: 'block',
        margin: '2em 0'
    }
});

interface MarkerJobOperationList {
    payload: JobRequest,
    name: string,
    group: string
}

interface Props extends WithStyles<typeof styles> {
    rootStore: RootStore;
    userTeam?: UserTeam;
}

interface States {
    errorMessage: string;
    runningJobs: RunningJobs[];
    currentJobsOffset: number;
    snackbarVariant: CCSnackbarVariant;
    snackbarMessage: string;
    snackbarOpen: boolean;
    isOpenCreateReportModal: boolean;
    teams: SimpleListItem[];
    treeFolders: RenderTree;
    treeFoldersClicked: string[];
    treeFoldersExpanded: string[];
    selectedFloorplan: EntityFloorplan | undefined;
    leafletPayload: LeafletPDFData | null;
    dialogTitle: string;
    dialogMessage: string;
    dialogOpen: boolean;
    showUpdateCategory: boolean;
    selectedUserName: string;
    selectedUserId: string;
    selectedUserTeams: SimpleListItem[];
    finalMarkerOperations: MarkerOperation[];
    isMarkerOperationsResume: boolean;
    updatedFileBlob: Blob | null;
    currentMarkerOperationsResumeOffset: number;
    isAdminDrawerOpen: boolean;
    selectedTabIndex: number;
    selectedTabDesc: TabDescriptor;
    isQRMarkersLoading: boolean;
    qrMarkerList: Marker[];
    qrTemplateFile: File | undefined;
    qrPositionFile: File | undefined;
    qrFontSize: number;
    isCMYKColorSpace: boolean;
    isQRSubmitLoading: boolean;
}

@inject('rootStore')
@observer
class AdminTab extends React.Component<Props, States> {
    public static defaultProps = {
    };

    state = {
        errorMessage: '',
        runningJobs: [] as RunningJobs[],
        currentJobsOffset: 0,
        snackbarVariant: 'info' as CCSnackbarVariant,
        snackbarMessage: '',
        snackbarOpen: false,
        isOpenCreateReportModal: false,
        teams: [] as SimpleListItem[],
        treeFolders: {
            id: 'treeRoot',
            name: 'Folders',
            children: [],
        },
        treeFoldersClicked: [],
        treeFoldersExpanded: [],
        selectedFloorplan: undefined as EntityFloorplan | undefined,
        leafletPayload: null,
        dialogTitle: '',
        dialogMessage: '',
        dialogOpen: false,
        showUpdateCategory: false,
        selectedUserName: '',
        selectedUserId: '',
        selectedUserTeams: [] as SimpleListItem[],
        finalMarkerOperations: [] as MarkerOperation[],
        isMarkerOperationsResume: false,
        updatedFileBlob: null,
        currentMarkerOperationsResumeOffset: 0,
        isAdminDrawerOpen: false,
        selectedTabIndex: 0,
        selectedTabDesc: {label: 'Marker Operations'} as TabDescriptor,
        qrMarkerList: [] as Marker[],
        isQRMarkersLoading: false,
        qrTemplateFile: undefined,
        qrPositionFile: undefined,
        qrFontSize: 10,
        isCMYKColorSpace: true,
        isQRSubmitLoading: false
    };

    clearMarkerOperations?: () => void = undefined;

    componentDidMount() {
        const {
            rootStore: { managerStore }
        } = this.props;
        const {
            error,
            currentAccount
        } = managerStore;

        if (currentAccount) {
            this.fetchFoldersTree();
            // TODO: DECIDE IF WE WANT TO FETCH TEAMS ON LOAD OR ON DEMAND WHEN SELECTING THE USER
            // this.getTeamsByAccount(currentAccount.accountId);
        }

        if (error) {
            this.showError(error);
            managerStore.error = '';
        }
    }

    showSnackbar = (
        snackbarMessage: string,
        snackbarVariant: CCSnackbarVariant
    ) => {
        this.setState({
            snackbarVariant,
            snackbarMessage,
            snackbarOpen: true,
        });
    };

    showError = (errorMessage: string | undefined) => {
        let message;
        if (errorMessage) {
            message = errorMessage;
        } else {
            message = 'Unknown Error';
        }

        this.showSnackbar(message, 'error' as CCSnackbarVariant);
    };

    showWarning = (warningMessage: string | undefined) => {
        if (!warningMessage) {
            return;
        }

        this.showSnackbar(warningMessage, 'warning' as CCSnackbarVariant);
    };

    showDialog = (dialogTitle: string, dialogMessage: string) => {
        this.setState({
            dialogTitle,
            dialogMessage,
            dialogOpen: true
        });
    };

    onToggleSideNavBar = () => {
        const { isAdminDrawerOpen } = this.state;
        this.setState({ isAdminDrawerOpen: !isAdminDrawerOpen});
    };

    fetchFoldersTree = () => {
        const {
            rootStore: { managerStore },
        } = this.props;

        managerStore.getAccountFolderTree(FOLDER_PERMISSIONS).then(data => {
            this.setState({
                treeFolders: data,
                treeFoldersClicked: [],
                treeFoldersExpanded: []
            });
        }).catch(error => {
            this.showError(error);
        });
    };

    getFolderNodes = (
        teamId: string,
        folderId: string
    ): Promise<RenderTree[]> => {
        const { LOCATION_FILTER_BUFFER_LIMIT: BUFFER_LIMIT } = REPORTS;
        const {
            rootStore: { managerStore },
        } = this.props;
        return managerStore
            .getFolderFloorplans(teamId, folderId, BUFFER_LIMIT)
            .then((results: SearchResults<Floorplan>) => {
                const {
                    rows: floorplans,
                    meta: { totalCount: totalFloorplans },
                } = results;

                if (totalFloorplans) {
                    return floorplans.map((floorplan: Floorplan) => {
                        // Add prefix to differentiate floorplans
                        // from the rest of the folder nodes
                        return {
                            id: `floorplan::${floorplan.id}`,
                            name: floorplan.name,
                        };
                    });
                } else {
                    this.showWarning(
                        'There are no folders or floorplans associated to this folder'
                    );
                    return [] as RenderTree[];
                }
            });
    };

    findNodeById = (root: RenderTree | null, id: string): RenderTree | null => {
        if (!root) return null;
    
        if (root.id === id) {
            return root;
        }
    
        if (root.children) {
            for (const child of root.children) {
                const foundNode = this.findNodeById(child, id);
                if (foundNode) {
                    return foundNode;
                }
            }
        }
    
        return null;
    };
    
    findChildrenById = (root: RenderTree | null, id: string): RenderTree[] => {
        const parentNode = this.findNodeById(root, id);
        if (parentNode && parentNode.children) {
            return parentNode.children;
        }
        return [];
    };

    fetchRunningJobs = () => {
        const {
            rootStore: { managerStore },
        } = this.props;

        const tasks = managerStore.currentStoredJobs.find(e => e.page === MainTabs.Admin);

        if (!tasks) {
            return;
        }

        this.setState({
            runningJobs: tasks.tasks
        }, () => {
            tasks.tasks.forEach(element => {
                if(element.status === 102) {
                    // SET TO RESTART POOLING
                    element.set_to_stop = false;
                    this.startTaskWatcher(element);
                }
            });
        });
    };

    startTaskWatcher = (task: RunningJobs) => {
        const { runningJobs } = this.state;

        if (!task.task_name || !task.task_id) {
            return;
        }

        const statusCallback = (taskId: string) => {
            const taskIndex = runningJobs.findIndex(e => e.task_id === taskId);
            const taskItem = runningJobs.find(e => e.task_id === taskId);

            if (!taskItem || taskItem.status !== 102) {
                return;
            }

            if (taskItem.set_to_stop) {
                return;
            }

            const {
                rootStore: { managerStore },
            } = this.props;

            managerStore.getRunningJobStatus(taskItem.task_id, true).then(data => {
                const endTime = new Date();
                taskItem.elapsedTime = Math.floor(Math.abs((endTime.getTime() - taskItem.startedAt) / 1000));
                if (data.status === StatusTypes.Error) {
                    taskItem.status = 500;
                    runningJobs[taskIndex] = taskItem;
                }
                if (data.status === StatusTypes.Complete) {
                    taskItem.status = 200;
                    taskItem.progress = 100.0;
                    const { result } = data;
                    if (!result) {
                        this.showError(
                            `Unexpected error occurred reporting - result key is undefined`
                        );
                    } else {
                        taskItem.result_data = {...taskItem.result_data as object, ...result};
                    }
                } else {
                    // KEEP TRACK OF THE PROGRESS STATUS IF ITS STILL THE SAME
                    if (taskItem.progress === data.completion) {
                        taskItem.static_progress_count += 1;
                    }
                    taskItem.progress = data.completion;
                    // IF WE REACH A POINT WHERE THE STATUS IS THE SAME THE JOB IS SLOW
                    // MAKE FEWER REQUESTS TO NOT OVERWHELM THE STATUS ENDPOINT
                    if (taskItem.static_progress_count === STATIC_PROGRESS_LIMIT) {
                        taskItem.timer += 1000;
                        taskItem.static_progress_count = 0;
                    }
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    setTimeout(taskItem.internalCallback!, taskItem.timer, taskId);
                }
                managerStore.setCurrentJobs(runningJobs, MainTabs.Admin);
                this.setState({runningJobs});
            }).catch((error) => {
                const { status } = error.response;
                // CHECK IF WE TIMEOUT OR NOT
                if (status === 408 || status === 504) {
                    // WE HAVE TIMEOUT INCREASE TIMER AND KEEP TRYING
                    taskItem.timer += 1000;
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    setTimeout(taskItem.internalCallback!, taskItem.timer, taskId);
                    return;
                }
                taskItem.status = 500;
                runningJobs[taskIndex] = taskItem;
                managerStore.setCurrentJobs(runningJobs, MainTabs.Admin);
                this.setState({runningJobs});
            });
        };

        // PREVENT GENERATING TIMERS WITHOUT PROPERTIES BEING INITIALIZED
        const taskIndex = runningJobs.findIndex(e => e.task_id === task.task_id);
        runningJobs[taskIndex].internalCallback = statusCallback;
        this.setState({runningJobs}, () => {
            setTimeout(statusCallback, task.timer, task.task_id);
        });
    };

    onJobsDataPageOverBoundaryReached = (boundary: PageBoundary, nextPage: number): Promise<void> => {
        return new Promise<void>(() => {
            const { 
                currentJobsOffset,
                runningJobs
            } = this.state;
            const totalTasks = runningJobs.length;
            const isFirstPage = nextPage === 0;
            const isLastPage = nextPage === (Math.ceil(totalTasks / TASKS_ROWS_PER_PAGE)-1);
            const newOffset = isFirstPage ? 0
                : isLastPage ? TASKS_PAGING_LIMIT * Math.floor(totalTasks / TASKS_PAGING_LIMIT)
                    : (boundary === PageBoundary.Upper) ? 
                        currentJobsOffset+TASKS_PAGING_LIMIT :
                        currentJobsOffset-TASKS_PAGING_LIMIT;
            const nextTasks = runningJobs;
            // MANUAL OFFSET
            this.setState({
                runningJobs: nextTasks.splice(newOffset, nextTasks.length)
            });
        });
    };

    onJobClick = (job: RunningJobs) => {
        if (job.status === 102) {
            return;
        }

        const {
            rootStore: { managerStore }
        } = this.props;

        if (job.status === 200 && job.type === AdminJobTypes.QRCodeGeneration) {
            managerStore.getRunningJobResult(job.task_id)
                .then((result: JobResult) => {
                    const qrResult = result.result as QRCodeGenerationJobResult;
                    window.open(qrResult?.url, '_blank');
                }).catch((error) => {
                    this.showError(
                        `Unexpected error occurred reporting - ${error}`
                    );
                });
            return;
        }

        managerStore.getRunningJobError(job.task_id, true)
            .then((response) => {
                this.showError(`An error occurred reporting for ${job.task_name} - ${response.error}`);
            }).catch((error) => {
                this.showError(
                    `Unexpected error occurred reporting - ${error}`
                );

            });
    };

    onJobClear = () => {
        const { runningJobs } = this.state;
        const pendingTasks = runningJobs.filter(e => e.status === 102);
        const {
            rootStore: { managerStore }
        } = this.props;

        managerStore.setCurrentJobs(pendingTasks, MainTabs.Admin);
        this.setState({ runningJobs: pendingTasks });
    };

    onTabChange = (selectedTabIndex: number, selectedTab: TabDescriptor) => {
        this.setState({
            selectedTabIndex,
            selectedTabDesc: selectedTab,
            selectedFloorplan: undefined as EntityFloorplan | undefined
        });
        this.clearQRMarkerList();
    };

    getTeamsByAccount = async (accountId: string) => {
        const {
            rootStore: { managerStore }
        } = this.props;

        managerStore.getTeamsByAccountId(accountId).then((teams) => {
            const items = teams.map((item: {
                team_id: string;
                name: string;
            }) => {
                return new SimpleListItem(item.team_id, item.name, item.team_id);
            });
            this.setState({ teams: items });
        }).catch((error) => {
            this.showError(
                `Unexpected error occurred reporting - ${error}`
            );
        });
    };

    getUserByEmail = async (email: string) => {
        const {
            rootStore: { managerStore }
        } = this.props;
        const { currentAccount } = managerStore;

        if (!currentAccount) {
            return;
        }

        managerStore.getUserByEmail(currentAccount.accountId, email).then((user) => {
            const { username, userId, teams } = user;
            const items = teams.map((item: {
                team_id: string;
            }) => {
                return new SimpleListItem(item.team_id, item.team_id, item.team_id);
            });
            this.setState({ 
                selectedUserName: username,
                selectedUserId: userId,
                selectedUserTeams: items
            });
        }).catch((error) => {
            this.showError(
                `Unexpected error occurred reporting - ${error}`
            );
        });
    };

    onSelectTeamClick = () => {
        console.log('click');
    };

    onFolderTreeClick = async (folderId: string): Promise<RenderTree[]> => {
        const { userTeam } = this.props;
        const { treeFoldersClicked, treeFolders } = this.state;
        
        let folderNodes = [] as RenderTree[];

        if (userTeam) {
            folderNodes = this.findChildrenById(treeFolders, folderId);

            if (folderNodes.length <= 0) {
                folderNodes = await this.getFolderNodes(userTeam.teamId, folderId);
            }
        }

        this.setState({
            treeFoldersClicked: [...treeFoldersClicked, folderId],
        });

        return folderNodes;
    };

    onFolderTreeToggle = (folderIds: string[]) => {
        this.setState({ treeFoldersExpanded: folderIds });
    };

    onSelectFloorplan = (selectedFloorplan: EntityFloorplan | undefined) => {
        const {
            rootStore: { 
                managerStore
            }
        } = this.props;
        const { selectedTabDesc } = this.state;

        if (!selectedFloorplan) {
            this.showWarning("The selected floorplan doesn't exist");
            return;
        }

        const { floorplan_id } = selectedFloorplan;

        if (selectedTabDesc.label === TabTypes.markerOperations) {
            this.setState({ leafletPayload: null });
            
            managerStore.getMarkersAndMapCoordinates(floorplan_id).then((leafletPayload: LeafletPDFData) => {
                this.setState({
                    selectedFloorplan,
                    leafletPayload
                });
            }).catch((error) => {
                this.showError(error);
            });
        } else if (selectedTabDesc.label === TabTypes.qrGeneration) {
            this.setState({isQRMarkersLoading: true});
            managerStore
                .getFloorplanMarkers(floorplan_id, 1000)
                .then((results: SearchResults<Marker>) => {
                    const {
                        rows
                    } = results;

                    this.setState({
                        qrMarkerList: rows,
                        isQRMarkersLoading: false
                    });
                });
        }

    };

    onFinishMarkerOperations = (markerOperations: MarkerOperation[], updatedFileBlob: Blob, clearMarkerOperations: () => void) => {
        this.clearMarkerOperations = clearMarkerOperations;
        this.setState({
            finalMarkerOperations: markerOperations,
            isMarkerOperationsResume: true,
            updatedFileBlob
        });
    };

    onMarkerResumePageOverBoundaryReached = (boundary: PageBoundary, nextPage: number): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            const { 
                currentMarkerOperationsResumeOffset,
                finalMarkerOperations
            } = this.state;
            const totalTasks = finalMarkerOperations.length;
            const isFirstPage = nextPage === 0;
            const isLastPage = nextPage === (Math.ceil(totalTasks / TASKS_ROWS_PER_PAGE)-1);
            const newOffset = isFirstPage ? 0
                : isLastPage ? ITEMS_PAGING_LIMIT * Math.floor(totalTasks / ITEMS_PAGING_LIMIT)
                    : (boundary === PageBoundary.Upper) ? 
                        currentMarkerOperationsResumeOffset+ITEMS_PAGING_LIMIT :
                        currentMarkerOperationsResumeOffset-ITEMS_PAGING_LIMIT;
            const nextTasks = finalMarkerOperations;
            // MANUAL OFFSET
            this.setState({
                finalMarkerOperations: nextTasks.splice(newOffset, nextTasks.length)
            });
        });
    };

    closeResumeModal = () => {
        this.setState({isMarkerOperationsResume: false});
    };

    onSubmitMarkerChanges = () => {
        const {
            rootStore: { managerStore }
        } = this.props;

        const {
            currentAccount,
            loggedInUser
        } = managerStore;

        const {
            finalMarkerOperations
        } = this.state;

        if (!currentAccount || !loggedInUser) {
            return;
        }

        // MAKE THE PAYLOADS
        const markerJobs: MarkerJobOperationList[] = finalMarkerOperations.map(e => {
            const payloadItem: JobRequest = {
                user_created_id: loggedInUser.userId,
                account_id: currentAccount.accountId,
                job_type: `${AdminJobTypes.MarkerOperations}`,
                additional_params: {...e}
            };

            const groupId = uuidv4();
            return {
                payload: payloadItem,
                name: `${e.verb}: ${e.name}`,
                group: groupId
            } as MarkerJobOperationList;
        });

        console.log(markerJobs);
        markerJobs.forEach(element => {
            this.submitMarkerJob(element.payload, element.name, element.group);
        });
    };

    submitMarkerJob = (jobPayload: JobRequest, exportName: string, groupId: string) => {
        const {
            rootStore: { managerStore }
        } = this.props;

        const { runningJobs, selectedFloorplan } = this.state;

        const {
            currentAccount
        } = managerStore;

        if (!currentAccount || !selectedFloorplan) {
            return;
        }

        managerStore.submitAdminJob(jobPayload).then((response: JobScheduleResult) => {
            const { jobId } = response;
            const newJob: RunningJobs = {
                task_name: `Saving marker changes: `,
                task_id: jobId,
                status: 102,
                type: AdminJobTypes.MarkerOperations,
                startedAt: Date.now(),
                elapsedTime: 0,
                progress: 0,
                timer: 1500,
                static_progress_count: 0,
                set_to_stop: false,
                result_data: {
                    name: 'folder and floorplan name'
                }
            };

            runningJobs.push(newJob);
            managerStore.setCurrentJobs(runningJobs, MainTabs.Admin);
            this.startTaskWatcher(newJob);
            if (this.clearMarkerOperations) {
                this.clearMarkerOperations();
            }
            this.setState({
                runningJobs,
                isAdminDrawerOpen: true,
                isMarkerOperationsResume: false
            });
        }).catch((error) => {
            this.showError(`ERROR: ${error}`);
        });
    };

    onRemoveQRMarker = (listItem: SimpleListItem) => {
        const { qrMarkerList } = this.state;

        const delIndex = qrMarkerList.findIndex(e => e.id === listItem.id);

        qrMarkerList.splice(delIndex, 1);

        this.setState({qrMarkerList});
    };

    clearQRMarkerList = () => {
        this.setState({qrMarkerList: []});
    };

    onQRFileChange = (type: 'qrPositionFile' | 'qrTemplateFile') => (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || !files.length) {
            return;
        }

        const newState: {qrPositionFile?: File, qrTemplateFile?: File} = {};
        newState[type] = files[0];
        this.setState(newState as States);
    };

    onFontSizeChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const qrFontSize = parseInt(event.target.value, 10);
        this.setState({ qrFontSize });
    };

    onColorSpaceChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        this.setState({ isCMYKColorSpace: checked });
    };

    onQRCodeGenerateClick = () => {
        const {
            rootStore: { managerStore }
        } = this.props;

        const {
            currentAccount,
            loggedInUser
        } = managerStore;

        const {
            qrMarkerList,
            qrTemplateFile,
            qrPositionFile,
            qrFontSize,
            isCMYKColorSpace,
            runningJobs
        } = this.state;

        if (qrMarkerList.length <= 0 || !qrPositionFile || !qrTemplateFile || !currentAccount || !loggedInUser) {
            return;
        }

        const markerIds = [] as string[];
        const markerNames = [] as string[];

        qrMarkerList.forEach((marker: Marker) => {
            markerIds.push(marker.id);
            markerNames.push(marker.name);
        });

        this.setState({isQRSubmitLoading: true});

        managerStore.uploadQRTemplateFiles(qrPositionFile, qrTemplateFile).then((response: QRTemplateResponse) => {
            const payloadItem: JobRequest = {
                user_created_id: loggedInUser.userId,
                account_id: currentAccount.accountId,
                job_type: `${AdminJobTypes.QRCodeGeneration}`,
                additional_params: {
                    marker_ids: markerIds,
                    marker_names: markerNames,
                    xfdf_path: response.position_url,
                    pdf_template_path: response.template_url,
                    font_size: qrFontSize,
                    color_space: isCMYKColorSpace ? 'CMYK' : 'sRGB'
                }
            };

            managerStore.submitAdminJob(payloadItem).then((response: JobScheduleResult) => {
                const { jobId } = response;
                const newJob: RunningJobs = {
                    task_name: `Generating QR Codes`,
                    task_id: jobId,
                    status: 102,
                    type: AdminJobTypes.QRCodeGeneration,
                    startedAt: Date.now(),
                    elapsedTime: 0,
                    progress: 0,
                    timer: 1500,
                    static_progress_count: 0,
                    set_to_stop: false
                };
                this.clearQRMarkerList();

                runningJobs.push(newJob);
                managerStore.setCurrentJobs(runningJobs, MainTabs.Admin);
                this.startTaskWatcher(newJob);
                this.setState({
                    runningJobs,
                    isAdminDrawerOpen: true,
                    isQRSubmitLoading: false
                });
            }).catch((error) => {
                this.showError(`ERROR: ${error}`);
            });
            
        }).catch((error) => {
            this.showError(`ERROR: ${error}`);
        });
    };

    public render() {
        const {
            classes,
            rootStore: {
                managerStore: {
                    isAdminToolTabLoading,
                    isFolderFloorplansLoading,
                    isTeamFoldersLoading,
                    isMapLoading,
                    adminToolTabs
                }
            }
        } = this.props;

        const {
            teams,
            treeFolders,
            treeFoldersClicked,
            treeFoldersExpanded,
            selectedFloorplan,
            leafletPayload,
            selectedUserName,
            selectedUserId,
            selectedUserTeams,
            isMarkerOperationsResume,
            finalMarkerOperations,
            runningJobs,
            currentJobsOffset,
            isAdminDrawerOpen,
            selectedTabIndex,
            qrMarkerList,
            isQRMarkersLoading,
            qrFontSize,
            isCMYKColorSpace,
            qrTemplateFile,
            qrPositionFile,
            isQRSubmitLoading
        } = this.state;

        const isTreeLoading = isFolderFloorplansLoading || isTeamFoldersLoading;

        const currentMarkerResume = finalMarkerOperations ? finalMarkerOperations.map((marker: MarkerOperation) => {
            return new SimpleListItem(marker.marker_id, `${marker.verb}: ${marker.name}`, marker.marker_id);
        }): [];
        const currentQRMarkers = qrMarkerList ? qrMarkerList.map((marker: Marker) => {
            return new SimpleListItem(marker.id, marker.name, marker.folderName);
        }): [];
        const isDisabled = (currentQRMarkers.length < 1);

        const tabList: TabDescriptor[] = adminToolTabs.map((tab) => {
            switch (tab) {
                case TabTypes.markerOperations:
                    return { label: TabTypes.markerOperations };
                case TabTypes.qrGeneration:
                    return { label: TabTypes.qrGeneration };
                case TabTypes.userManagement:
                    return { label: TabTypes.userManagement };
                default:
                    throw new Error(`Unknown tab type: ${tab}`);
            }
        });

        return (
            <div className={classes.root}>
                <SideNavbar
                    classes={{
                        root: classes.navbarRoot,
                        drawerToggleButton: classes.navbarToogleButton,
                    }}
                    isDrawerOpen={isAdminDrawerOpen}
                    collapsedArea={
                        <div
                            className={`${classes.navbarIconContainer} ${classes.navbarIconContainerSmall}`}
                        >
                            <HourglassBottom className={classes.navbarIcon} />
                        </div>
                    }
                    onExpanderClick={this.onToggleSideNavBar}
                >
                    <div className={`${classes.contentContainer} ${classes.navbarContent}`}>
                        <div className={classes.navbarTitleContainer}>
                            <div className={classes.navbarIconContainer}>
                                <HourglassBottom className={classes.navbarIcon} />
                            </div>
                            <Typography
                                className={classes.navbarTitle}
                                variant="subtitle1"
                            >
                                Operations Processing
                            </Typography>
                        </div>
                        <Divider className={classes.divider} />
                        <RunningJobsList
                            className={classes.tasksContainerSize}
                            classes={{
                                buttonClear: classes.routeNavButton
                            }}
                            tasks={runningJobs}
                            rowsPerPage={TASKS_ROWS_PER_PAGE}
                            taskItemsOffset={currentJobsOffset}
                            onJobsDataPageOverBoundary={this.onJobsDataPageOverBoundaryReached}
                            onJobClear={this.onJobClear}
                            onJobClick={this.onJobClick}
                        />
                    </div>
                </SideNavbar>
                <div className={classes.tabContainer}>
                    <TabControl
                        classes={{
                            root: classes.tabRoot
                        }}
                        tabsDescriptors={tabList}
                        selectedTab={selectedTabIndex}
                        onTabChange={this.onTabChange}
                    >
                        {
                            adminToolTabs.map((tab) => {
                                switch (tab) {
                                    case TabTypes.markerOperations:
                                        return (
                                            <div className={classes.treeContainer}>
                                                <div className={classes.treeSection}>
                                                    <Typography
                                                        className={classes.treeTitle}
                                                        variant="h5"
                                                    >
                                                        Select Floor
                                                    </Typography>
                                                    <FolderTree
                                                        folderTreeData={treeFolders}
                                                        foldersClicked={treeFoldersClicked}
                                                        foldersExpanded={treeFoldersExpanded}
                                                        onFolderTreeClick={this.onFolderTreeClick}
                                                        onFolderTreeToggle={this.onFolderTreeToggle}
                                                        onSelectFloorplan={this.onSelectFloorplan}
                                                        isLoading={isTreeLoading}
                                                    />
                                                </div>
                                                {
                                                    selectedFloorplan && leafletPayload ?
                                                        <LeafletPDFClient
                                                            className={`${classes.animContainer} ${classes.thinClientContainer}`}
                                                            classes={{fullScreenContainer: classes.fullScreenContainer}}
                                                            floorplanName={selectedFloorplan.name}
                                                            floorplanId={selectedFloorplan.floorplan_id}
                                                            floorplanPayload={leafletPayload!}
                                                            onFinishMarkerOperations={this.onFinishMarkerOperations}
                                                        />
                                                        : <div className={classes.mapWrapper}></div>
                                                }
                                            </div>
                                        );
                                    case TabTypes.qrGeneration:
                                        return (
                                            <div className={classes.treeContainer}>
                                                <div className={classes.QRSection}>
                                                    <Typography
                                                        className={classes.treeTitle}
                                                        variant="h5"
                                                    >
                                                        Select Floor
                                                    </Typography>
                                                    <FolderTree
                                                        folderTreeData={treeFolders}
                                                        foldersClicked={treeFoldersClicked}
                                                        foldersExpanded={treeFoldersExpanded}
                                                        onFolderTreeClick={this.onFolderTreeClick}
                                                        onFolderTreeToggle={this.onFolderTreeToggle}
                                                        onSelectFloorplan={this.onSelectFloorplan}
                                                        isLoading={isTreeLoading}
                                                    />
                                                </div>
                                                <div className={classes.QRSection}>
                                                    <Typography
                                                        className={classes.treeTitle}
                                                        variant="h5"
                                                    >
                                                        Marker List
                                                    </Typography>
                                                    <SimpleListPagination
                                                        className={classes.simpleList}
                                                        keepItemSelected={false}
                                                        items={currentQRMarkers}
                                                        isLoading={isQRMarkersLoading}
                                                        totalItems={qrMarkerList.length}
                                                        rowsPerPage={TASKS_ROWS_PER_PAGE}
                                                        noItemsLabel='No markers loaded'
                                                        canDeleteListItem={true}
                                                        onDeleteItemClick={this.onRemoveQRMarker}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        disabled={qrMarkerList.length <= 0}
                                                        onClick={this.clearQRMarkerList}
                                                    >
                                                        Clear Markers
                                                    </Button>
                                                </div>
                                                <div className={classes.QRSectionSmall}>
                                                    <h4>Template File:</h4>
                                                    <input
                                                        type="file"
                                                        id="template-file"
                                                        className="input-file"
                                                        data-testid="template-file"
                                                        accept=".pdf"
                                                        disabled={isDisabled}
                                                        onChange={this.onQRFileChange('qrTemplateFile')}
                                                    />
                                                    <TextField
                                                        className={classes.fontInput}
                                                        label="Font Size"
                                                        placeholder="Template"
                                                        variant="standard"
                                                        value={qrFontSize}
                                                        disabled={isDisabled}
                                                        onChange={this.onFontSizeChanged}
                                                        margin="normal"
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                    />
                                                    <h4>QR Code Position File:</h4>
                                                    <input
                                                        type="file"
                                                        id="position-file"
                                                        data-testid="position-file"
                                                        className="input-file"
                                                        accept=".xfdf"
                                                        disabled={isDisabled}
                                                        onChange={this.onQRFileChange('qrPositionFile')}
                                                    />
                                                    <br />
                                                    <h4>PDF Color space:</h4>
                                                    <p className={classes.tooltipText}>
                                                        CMYK is the default option for most templates, if colors look muddy/missmatched you can switch to sRGB
                                                    </p>
                                                    <Typography component="div">
                                                        <Grid component="label" container alignItems="center" spacing={1}>
                                                            <Grid item>
                                                                <b>
                                                                    sRGB
                                                                </b>
                                                            </Grid>
                                                            <Grid item>
                                                                <Switch
                                                                    color="primary"
                                                                    checked={isCMYKColorSpace}
                                                                    onChange={this.onColorSpaceChange}
                                                                    name="pdf-color-space"
                                                                    id="pdf-color-space-input"
                                                                    data-testid="pdf-color-space-input"
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                <b>
                                                                    CMYK
                                                                </b>
                                                            </Grid>
                                                        </Grid>
                                                    </Typography>
                                                    <br />
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        disabled={(isDisabled || isQRSubmitLoading || (qrMarkerList.length < 1) || !qrTemplateFile || !qrPositionFile)}
                                                        onClick={this.onQRCodeGenerateClick}
                                                    >
                                                        Generate QR Codes
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    case TabTypes.userManagement:
                                        return (
                                            <div className={classes.addTeamContainer}>
                                                <Typography
                                                    className={classes.bodyTitle}
                                                    variant="h4">
                                                        Select user
                                                </Typography>
                                                <SearchInput
                                                    classes={{ root: classes.searchRoot }}
                                                    placeholder="Email"
                                                    buttonText="Find User"
                                                    searchIconButtonDisabled={false}
                                                    onSearchClick={this.getUserByEmail}
                                                />
                                                <Divider className={classes.divider} />
                                                {
                                                    selectedUserId !== '' && selectedUserName !== '' &&
                                                    <>
                                                        <Paper elevation={2} sx={{ marginTop: '2em', marginBottom: '2em' }}>
                                                            <CardContent>
                                                                <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 14 }}>
                                                                    { selectedUserId }
                                                                </Typography>
                                                                <Typography variant="h5" component="div">
                                                                    { selectedUserName }
                                                                </Typography>
                                                                <Typography sx={{ color: 'text.secondary', mb: 1.5 }}>Current Teams</Typography>
                                                                <SimpleList
                                                                    items={selectedUserTeams}
                                                                    onListItemClick={this.onSelectTeamClick}
                                                                />
                                                            </CardContent>
                                                            <CardActions>
                                                                <Button size="small">Add Team</Button>
                                                            </CardActions>
                                                        </Paper>
                                                        <SimpleList
                                                            isLoading={isAdminToolTabLoading}
                                                            items={teams}
                                                            onListItemClick={this.onSelectTeamClick}
                                                        />
                                                    </>
                                                }
                                            </div>
                                        );
                                    default:
                                        return <div>
                                            <Typography
                                                variant="h4"
                                            >
                                                Error: Unknown tab type: {tab}
                                            </Typography>
                                        </div>;
                                }
                            })
                        }
                    </TabControl>
                    <SimpleModal
                        className={classes.modal}
                        open={isMarkerOperationsResume}
                        buttonOkLabel=""
                        buttonCancelLabel=""
                    >
                        <Typography
                            className={classes.modalTitle}
                            variant="h5"
                        >
                            Marker operations resume
                        </Typography>
                        <div className={classes.modalContainer}>
                            <Typography variant="subtitle1">
                                Please review the marker changes before submitting, 
                                once you are sure they are correct click on the submit button
                            </Typography>
                            <SimpleListPagination
                                className={classes.operationsList}
                                keepItemSelected={true}
                                items={currentMarkerResume}
                                isLoading={false}
                                rowsPerPage={TASKS_ROWS_PER_PAGE}
                                totalItems={currentMarkerResume.length}
                                offset={0}
                                onPageOverBoundary={this.onMarkerResumePageOverBoundaryReached}
                            />
                            <div
                                className={classes.modalFooter}
                            >
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={this.closeResumeModal}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.onSubmitMarkerChanges}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </SimpleModal>
                    <CCSpinner
                        className={classes.spinner}
                        loading={isMapLoading || isTeamFoldersLoading || isQRSubmitLoading}
                        overlayVisible={true}
                        size={100}
                    />
                </div>
            </div>
        );
    }
}

const MUIComponent = withStyles(styles)(AdminTab);
export { MUIComponent as AdminTab };
